import { CommonModule } from "@angular/common";
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { NgGlyph } from "@ng-icons/core";
import { FileUploaderItem, FileUploaderViewMode } from "./Dto/FileUploaderItem";
import { AppButtonComponent } from "../Button/ButtonComponent";

@Component({
  selector: "app-file-uploader-base",
  standalone: true,
  imports: [CommonModule, AppButtonComponent, NgGlyph],
  templateUrl: "./FileUploaderComponent.html",
  styleUrl: "./FileUploaderComponent.scss",
})
export class FileUploaderBaseComponent {
  @ViewChild("fileInput", { static: true }) fileInput?: ElementRef<HTMLInputElement>;

  @Input() multiple = false;
  @Input() accept = "*/*";
  @Input() disabled = false;
  @Input() items: FileUploaderItem[] = [];
  @Input() isUploading = false;
  @Input() viewMode: FileUploaderViewMode = "list";
  @Input() errorMessage: string | null = null;
  @Input() label = "Upload files";
  @Input() hint = "Drag and drop files here, or click to browse.";
  @Input() emptyStateTitle = "No files uploaded";
  @Input() emptyStateDescription = "Uploaded files will appear here after they finish uploading.";

  @Output() filesSelected = new EventEmitter<globalThis.File[]>();
  @Output() removeRequested = new EventEmitter<string>();

  isDragOver = false;
  private readonly brokenPreviewIds = new Set<string>();

  openFilePicker(): void {
    if (this.isInteractionDisabled()) {
      return;
    }

    this.fileInput?.nativeElement.click();
  }

  onDropzoneKeydown(event: KeyboardEvent): void {
    if (event.key !== "Enter") {
      if (event.key !== " ") {
        return;
      }
    }

    event.preventDefault();
    this.openFilePicker();
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const fileList = input.files;
    if (!fileList?.length) {
      return;
    }

    this.emitSelectedFiles(Array.from(fileList));
    this.clearNativeInput();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (this.isInteractionDisabled()) {
      return;
    }

    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;

    if (this.isInteractionDisabled()) {
      return;
    }

    const files = Array.from(event.dataTransfer?.files ?? []);
    if (!files.length) {
      return;
    }

    this.emitSelectedFiles(files);
  }

  requestRemove(itemId: string, event?: Event): void {
    event?.stopPropagation();
    if (this.isInteractionDisabled()) {
      return;
    }

    this.removeRequested.emit(itemId);
  }

  openInNewTab(item: FileUploaderItem, event?: Event): void {
    event?.stopPropagation();

    const publicUrl = this.getItemPublicUrl(item);
    if (!publicUrl) {
      return;
    }

    window.open(publicUrl, "_blank", "noopener,noreferrer");
  }

  onPreviewError(itemId: string): void {
    this.brokenPreviewIds.add(itemId);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) {
      return "0 Bytes";
    }

    const units = ["Bytes", "KB", "MB", "GB"];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, index);
    return `${parseFloat(value.toFixed(2))} ${units[index]}`;
  }

  getDropzoneTabIndex(): number {
    if (this.isInteractionDisabled()) {
      return -1;
    }

    return 0;
  }

  getAriaDisabled(): boolean {
    return this.isInteractionDisabled();
  }

  getDropzoneIconName(): string {
    if (this.isUploading) {
      return "sync";
    }

    return "upload_file";
  }

  getHintText(): string {
    if (this.isUploading) {
      return "Uploading files...";
    }

    return this.hint;
  }

  getChooserLabel(): string {
    if (this.multiple) {
      return "Choose Files";
    }

    return "Choose File";
  }

  getItemIconName(item: FileUploaderItem): string {
    if (item.status === "uploading") {
      return "progress_activity";
    }

    return "description";
  }

  getItemStatusText(item: FileUploaderItem): string {
    if (item.status === "uploading") {
      return "Uploading";
    }

    return "Uploaded";
  }

  isThumbnailMode(): boolean {
    if (this.viewMode === "thumbnail") {
      return true;
    }

    if (this.viewMode === "mixed") {
      return true;
    }

    return false;
  }

  shouldShowImagePreview(item: FileUploaderItem): boolean {
    if (this.brokenPreviewIds.has(item.id)) {
      return false;
    }

    const publicUrl = this.getItemPublicUrl(item);
    if (!publicUrl) {
      return false;
    }

    if (!this.isImageItem(item)) {
      return false;
    }

    return true;
  }

  getPreviewUrl(item: FileUploaderItem): string {
    return this.getItemPublicUrl(item);
  }

  isRemoveDisabled(item: FileUploaderItem): boolean {
    if (this.isInteractionDisabled()) {
      return true;
    }

    if (item.status === "uploading") {
      return true;
    }

    return false;
  }

  shouldShowOpenAction(item: FileUploaderItem): boolean {
    const publicUrl = this.getItemPublicUrl(item);
    if (!publicUrl) {
      return false;
    }

    if (item.status === "uploading") {
      return false;
    }

    return true;
  }

  private getItemPublicUrl(item: FileUploaderItem): string {
    if (typeof item.publicUrl === "string") {
      return item.publicUrl.trim();
    }

    return "";
  }

  private isImageItem(item: FileUploaderItem): boolean {
    const mimeType = this.normalizeMimeType(item.mimeType);
    if (mimeType.startsWith("image/")) {
      return true;
    }

    const fileName = item.name.toLowerCase();
    return (
      fileName.endsWith(".png") ||
      fileName.endsWith(".jpg") ||
      fileName.endsWith(".jpeg") ||
      fileName.endsWith(".gif") ||
      fileName.endsWith(".webp") ||
      fileName.endsWith(".svg") ||
      fileName.endsWith(".bmp") ||
      fileName.endsWith(".avif")
    );
  }

  private normalizeMimeType(value: string | undefined): string {
    if (typeof value === "string") {
      return value.trim().toLowerCase();
    }

    return "";
  }

  private emitSelectedFiles(files: globalThis.File[]): void {
    if (this.isInteractionDisabled()) {
      return;
    }

    const normalizedFiles = this.normalizeSelectedFiles(files);
    if (!normalizedFiles.length) {
      return;
    }

    this.filesSelected.emit(normalizedFiles);
  }

  private clearNativeInput(): void {
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = "";
    }
  }

  private isInteractionDisabled(): boolean {
    if (this.disabled) {
      return true;
    }

    if (this.isUploading) {
      return true;
    }

    return false;
  }

  private normalizeSelectedFiles(files: globalThis.File[]): globalThis.File[] {
    if (!this.multiple) {
      return files.slice(0, 1);
    }

    return files;
  }
}
